/**
 * Real API Integrations for Live Scam Data
 * This module fetches scam data from multiple external sources
 */

import axios from 'axios';

// API Keys from environment variables
const NEWS_API_KEY = process.env.REACT_APP_NEWS_API_KEY;
const CRYPTOCOMPARE_API_KEY = process.env.REACT_APP_CRYPTOCOMPARE_API_KEY;
const REDDIT_CLIENT_ID = process.env.REACT_APP_REDDIT_CLIENT_ID;
const REDDIT_CLIENT_SECRET = process.env.REACT_APP_REDDIT_CLIENT_SECRET;

/**
 * Fetch scam reports from NewsAPI
 * Free tier: 100 requests/day, 1000/month
 * Sign up: https://newsapi.org/
 */
export async function fetchScamsFromNews() {
  if (!NEWS_API_KEY) {
    console.warn('NewsAPI key not configured');
    return [];
  }

  try {
    // Search for crypto scam news from past 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const response = await axios.get('https://newsapi.org/v2/everything', {
      params: {
        q: 'cryptocurrency scam OR crypto fraud OR bitcoin scam OR NFT scam OR ' +
           'pig butchering OR romance scam crypto OR ' +
           'rug pull OR exit scam OR ponzi scheme crypto OR ' +
           'fake crypto exchange OR phishing crypto OR ' +
           'crypto investment fraud OR DeFi exploit OR ' +
           'crypto wallet scam OR impersonation scam crypto OR ' +
           'pump and dump crypto OR crypto money laundering OR ' +
           'cryptocurrency theft OR bitcoin fraud OR ethereum scam',
        language: 'en',
        sortBy: 'publishedAt',
        from: thirtyDaysAgo.toISOString().split('T')[0],
        pageSize: 30,  // Increased to get more results
        apiKey: NEWS_API_KEY
      }
    });

    return response.data.articles.map(article => ({
      title: article.title,
      description: article.description || article.content?.substring(0, 200) + '...',
      date: new Date(article.publishedAt),
      url: article.url,
      source: article.source.name,
      location: extractLocationFromText(article.title + ' ' + article.description),
      type: categorizeScamFromText(article.title + ' ' + article.description),
      severity: 'medium',
      verified: true,
      amount: 0,
      victimCount: 0
    }));
  } catch (error) {
    console.error('Error fetching from NewsAPI:', error.message);
    return [];
  }
}

/**
 * Fetch crypto scam reports from Reddit
 * Uses Reddit's public JSON API (no auth required for read-only)
 */
export async function fetchScamsFromReddit() {
  try {
    const subreddits = ['cryptocurrency', 'Bitcoin', 'CryptoScams', 'Scams'];
    const allPosts = [];

    for (const subreddit of subreddits) {
      try {
        const response = await axios.get(
          `https://www.reddit.com/r/${subreddit}/search.json`,
          {
            params: {
              q: 'scam OR fraud OR stolen OR lost OR ' +
                 '"pig butchering" OR "romance scam" OR ' +
                 '"rug pull" OR "exit scam" OR ponzi OR ' +
                 'phishing OR "fake exchange" OR hacked OR ' +
                 '"pump and dump" OR rugpull OR honeypot',
              restrict_sr: 'on',
              sort: 'new',
              limit: 15,  // Increased limit
              t: 'week'
            },
            headers: {
              'User-Agent': 'CFE-Portal/1.0'
            }
          }
        );

        const posts = response.data.data.children.map(post => ({
          title: post.data.title,
          description: post.data.selftext?.substring(0, 300) + '...' || '',
          date: new Date(post.data.created_utc * 1000),
          url: `https://reddit.com${post.data.permalink}`,
          source: `r/${subreddit}`,
          location: extractLocationFromText(post.data.title + ' ' + post.data.selftext),
          type: categorizeScamFromText(post.data.title),
          severity: post.data.score > 100 ? 'high' : 'medium',
          verified: false,
          amount: extractAmountFromText(post.data.title + ' ' + post.data.selftext),
          victimCount: 1
        }));

        allPosts.push(...posts);
      } catch (error) {
        console.error(`Error fetching from r/${subreddit}:`, error.message);
      }
    }

    return allPosts;
  } catch (error) {
    console.error('Error fetching from Reddit:', error.message);
    return [];
  }
}

/**
 * Fetch scam data from CryptoCompare Social Data
 * Free tier available with API key
 */
export async function fetchScamsFromCryptoCompare() {
  if (!CRYPTOCOMPARE_API_KEY) {
    console.warn('CryptoCompare API key not configured');
    return [];
  }

  try {
    // Get latest crypto news with negative sentiment
    const response = await axios.get('https://min-api.cryptocompare.com/data/v2/news/', {
      params: {
        categories: 'Regulation,Crime',
        lang: 'EN',
        api_key: CRYPTOCOMPARE_API_KEY
      }
    });

    return response.data.Data.slice(0, 15).map(article => ({
      title: article.title,
      description: article.body.substring(0, 200) + '...',
      date: new Date(article.published_on * 1000),
      url: article.url,
      source: article.source,
      location: extractLocationFromText(article.title + ' ' + article.body),
      type: categorizeScamFromText(article.title + ' ' + article.body),
      severity: article.categories.includes('Crime') ? 'high' : 'medium',
      verified: true,
      amount: 0,
      victimCount: 0
    }));
  } catch (error) {
    console.error('Error fetching from CryptoCompare:', error.message);
    return [];
  }
}

/**
 * Fetch scam addresses from ChainAbuse API
 * Public API - no key required
 */
export async function fetchScamsFromChainAbuse() {
  try {
    const response = await axios.get('https://www.chainabuse.com/api/reports', {
      params: {
        limit: 20,
        offset: 0
      },
      headers: {
        'Accept': 'application/json'
      }
    });

    return response.data.reports?.map(report => ({
      title: `${report.subcategory || 'Blockchain'} Scam - ${report.address?.substring(0, 10)}...`,
      description: report.description || 'Reported cryptocurrency address involved in fraudulent activity',
      date: new Date(report.created_at),
      url: `https://www.chainabuse.com/report/${report.id}`,
      source: 'ChainAbuse',
      location: report.reporter_country || 'Unknown',
      type: mapChainAbuseCategory(report.category),
      severity: 'high',
      verified: true,
      amount: 0,
      victimCount: 1
    })) || [];
  } catch (error) {
    console.error('Error fetching from ChainAbuse:', error.message);
    return [];
  }
}

/**
 * Fetch known scam websites from CryptoScamDB
 * Public domain list - no API key needed
 */
export async function fetchScamsFromCryptoScamDB() {
  try {
    const response = await axios.get('https://api.cryptoscamdb.org/v1/scams');

    const recentScams = response.data.result
      .filter(scam => scam.entries && scam.entries.length > 0)
      .slice(0, 20)
      .map(scam => ({
        title: scam.name || 'Cryptocurrency Scam',
        description: scam.description || `Scam type: ${scam.type}. Category: ${scam.category}`,
        date: new Date(scam.entries[0].timestamp || Date.now()),
        url: scam.entries[0].url || scam.url || '#',
        source: 'CryptoScamDB',
        location: 'Global',
        type: mapCryptoScamDBType(scam.type),
        severity: scam.type === 'scam' ? 'critical' : 'high',
        verified: true,
        amount: 0,
        victimCount: 0
      }));

    return recentScams;
  } catch (error) {
    console.error('Error fetching from CryptoScamDB:', error.message);
    return [];
  }
}

/**
 * Fetch scam news from CoinGecko
 * FREE - No API key required!
 * Provides crypto news from 500+ sources
 */
export async function fetchScamsFromCoinGecko() {
  try {
    const response = await axios.get('https://api.coingecko.com/api/v3/news', {
      headers: {
        'Accept': 'application/json'
      }
    });

    // Filter for scam/fraud related news
    const scamNews = response.data.data
      .filter(article => {
        const text = (article.title + ' ' + article.description).toLowerCase();
        return text.includes('scam') ||
               text.includes('fraud') ||
               text.includes('hack') ||
               text.includes('stolen') ||
               text.includes('exploit') ||
               text.includes('rug pull') ||
               text.includes('rugpull') ||
               text.includes('ponzi') ||
               text.includes('pig butchering') ||
               text.includes('romance scam') ||
               text.includes('phishing') ||
               text.includes('fake exchange') ||
               text.includes('exit scam') ||
               text.includes('pump and dump') ||
               text.includes('wallet drain') ||
               text.includes('theft') ||
               text.includes('laundering') ||
               text.includes('honeypot');
      })
      .slice(0, 15)
      .map(article => ({
        title: article.title,
        description: article.description?.substring(0, 250) + '...' || 'No description available',
        date: new Date(article.updated_at),
        url: article.url,
        source: article.news_site || 'CoinGecko',
        location: extractLocationFromText(article.title + ' ' + article.description),
        type: categorizeScamFromText(article.title + ' ' + article.description),
        severity: 'medium',
        verified: true,
        amount: extractAmountFromText(article.title + ' ' + article.description),
        victimCount: 0
      }));

    return scamNews;
  } catch (error) {
    console.error('Error fetching from CoinGecko:', error.message);
    return [];
  }
}

/**
 * Fetch aggregated crypto scam news from CryptoPanic
 * FREE - No API key required!
 * Aggregates news from 1000+ crypto sources
 */
export async function fetchScamsFromCryptoPanic() {
  try {
    const response = await axios.get('https://cryptopanic.com/api/v1/posts/', {
      params: {
        auth_token: 'public', // Public access
        filter: 'important',
        kind: 'news',
        currencies: 'BTC,ETH'
      },
      headers: {
        'Accept': 'application/json'
      }
    });

    // Filter for scam/fraud related posts
    const scamPosts = response.data.results
      .filter(post => {
        const text = (post.title + ' ' + (post.body || '')).toLowerCase();
        return text.includes('scam') ||
               text.includes('fraud') ||
               text.includes('hack') ||
               text.includes('stolen') ||
               text.includes('exploit') ||
               text.includes('rug pull') ||
               text.includes('rugpull') ||
               text.includes('attack') ||
               text.includes('pig butchering') ||
               text.includes('romance scam') ||
               text.includes('phishing') ||
               text.includes('ponzi') ||
               text.includes('exit scam') ||
               text.includes('fake exchange') ||
               text.includes('pump and dump') ||
               text.includes('wallet drain') ||
               text.includes('theft') ||
               text.includes('honeypot');
      })
      .slice(0, 15)
      .map(post => ({
        title: post.title,
        description: post.body?.substring(0, 250) || 'Breaking crypto security news',
        date: new Date(post.published_at),
        url: post.url,
        source: post.source?.title || 'CryptoPanic',
        location: extractLocationFromText(post.title + ' ' + (post.body || '')),
        type: categorizeScamFromText(post.title),
        severity: post.votes?.important > 0 ? 'high' : 'medium',
        verified: true,
        amount: extractAmountFromText(post.title + ' ' + (post.body || '')),
        victimCount: 0
      }));

    return scamPosts;
  } catch (error) {
    console.error('Error fetching from CryptoPanic:', error.message);
    return [];
  }
}

/**
 * Fetch government/official scam warnings
 * Uses public government RSS feeds and APIs
 */
export async function fetchGovernmentScamWarnings() {
  try {
    // Example: FTC (Federal Trade Commission) scam alerts
    // Note: This is a mock - actual FTC API requires parsing RSS/XML
    const response = await axios.get('https://www.consumer.ftc.gov/articles/rss/feed.xml', {
      headers: {
        'Accept': 'application/xml'
      }
    });

    // Parse XML to JSON (you'd need a library like xml2js)
    // For now, returning empty array - implement XML parsing if needed
    return [];
  } catch (error) {
    console.error('Error fetching government warnings:', error.message);
    return [];
  }
}

/**
 * Aggregate all external scam data sources
 */
export async function aggregateExternalScamData() {
  console.log('Fetching live scam data from external APIs...');

  try {
    const [
      newsScams,
      redditScams,
      cryptoCompareScams,
      chainAbuseScams,
      cryptoScamDBScams,
      coinGeckoScams,
      cryptoPanicScams
    ] = await Promise.allSettled([
      fetchScamsFromNews(),
      fetchScamsFromReddit(),
      fetchScamsFromCryptoCompare(),
      fetchScamsFromChainAbuse(),
      fetchScamsFromCryptoScamDB(),
      fetchScamsFromCoinGecko(),
      fetchScamsFromCryptoPanic()
    ]);

    const allScams = [
      ...(newsScams.status === 'fulfilled' ? newsScams.value : []),
      ...(redditScams.status === 'fulfilled' ? redditScams.value : []),
      ...(cryptoCompareScams.status === 'fulfilled' ? cryptoCompareScams.value : []),
      ...(chainAbuseScams.status === 'fulfilled' ? chainAbuseScams.value : []),
      ...(cryptoScamDBScams.status === 'fulfilled' ? cryptoScamDBScams.value : []),
      ...(coinGeckoScams.status === 'fulfilled' ? coinGeckoScams.value : []),
      ...(cryptoPanicScams.status === 'fulfilled' ? cryptoPanicScams.value : [])
    ];

    // Log source breakdown
    const sourceCount = {
      'NewsAPI': newsScams.status === 'fulfilled' ? newsScams.value.length : 0,
      'Reddit': redditScams.status === 'fulfilled' ? redditScams.value.length : 0,
      'CryptoCompare': cryptoCompareScams.status === 'fulfilled' ? cryptoCompareScams.value.length : 0,
      'ChainAbuse': chainAbuseScams.status === 'fulfilled' ? chainAbuseScams.value.length : 0,
      'CryptoScamDB': cryptoScamDBScams.status === 'fulfilled' ? cryptoScamDBScams.value.length : 0,
      'CoinGecko': coinGeckoScams.status === 'fulfilled' ? coinGeckoScams.value.length : 0,
      'CryptoPanic': cryptoPanicScams.status === 'fulfilled' ? cryptoPanicScams.value.length : 0
    };

    console.log(`Fetched ${allScams.length} scams from 7 external sources:`, sourceCount);
    return allScams;
  } catch (error) {
    console.error('Error aggregating external scam data:', error);
    return [];
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Extract location mentions from text
 */
function extractLocationFromText(text) {
  if (!text) return null;

  // Common country patterns
  const countries = [
    'United States', 'USA', 'UK', 'United Kingdom', 'China', 'India', 'Japan',
    'Germany', 'France', 'Canada', 'Australia', 'Singapore', 'Hong Kong',
    'South Korea', 'Russia', 'Brazil', 'Mexico', 'Nigeria', 'Philippines'
  ];

  const lowerText = text.toLowerCase();
  for (const country of countries) {
    if (lowerText.includes(country.toLowerCase())) {
      return country;
    }
  }

  return null;
}

/**
 * Extract dollar amounts from text
 */
function extractAmountFromText(text) {
  if (!text) return 0;

  // Look for patterns like $1M, $5000, $10 million, etc.
  const patterns = [
    /\$(\d+\.?\d*)\s*(million|M)/i,
    /\$(\d+\.?\d*)\s*(billion|B)/i,
    /\$(\d+\.?\d*)\s*(thousand|K)/i,
    /\$(\d+,?\d*)/
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const amount = parseFloat(match[1].replace(/,/g, ''));
      const unit = match[2]?.toLowerCase();

      if (unit === 'million' || unit === 'm') return amount * 1000000;
      if (unit === 'billion' || unit === 'b') return amount * 1000000000;
      if (unit === 'thousand' || unit === 'k') return amount * 1000;
      return amount;
    }
  }

  return 0;
}

/**
 * Categorize scam type from text content
 */
function categorizeScamFromText(text) {
  if (!text) return 'Other';

  const lowerText = text.toLowerCase();

  // Check for specific scam types (order matters - most specific first)
  if (lowerText.includes('pig butchering')) return 'Pig Butchering';
  if (lowerText.includes('pig-butchering')) return 'Pig Butchering';
  if (lowerText.includes('rug pull') || lowerText.includes('rugpull')) return 'Rug Pull';
  if (lowerText.includes('romance scam')) return 'Romance Scam';
  if (lowerText.includes('ponzi')) return 'Ponzi Scheme';
  if (lowerText.includes('phishing')) return 'Phishing';
  if (lowerText.includes('fake ico') || lowerText.includes('fraudulent ico')) return 'Fake ICO';
  if (lowerText.includes('pump') && lowerText.includes('dump')) return 'Pump & Dump';
  if (lowerText.includes('fake exchange') || lowerText.includes('fraudulent exchange')) return 'Fake Exchange';
  if (lowerText.includes('investment fraud') || lowerText.includes('investment scam')) return 'Investment Fraud';
  if (lowerText.includes('nft scam')) return 'NFT Scam';
  if (lowerText.includes('defi') && lowerText.includes('exploit')) return 'DeFi Exploit';
  if (lowerText.includes('exit scam')) return 'Exit Scam';
  if (lowerText.includes('honeypot')) return 'Honeypot';
  if (lowerText.includes('wallet drain')) return 'Wallet Drain';
  if (lowerText.includes('impersonation')) return 'Impersonation';
  if (lowerText.includes('money laundering') || lowerText.includes('laundering')) return 'Money Laundering';

  return 'Other';
}

/**
 * Map ChainAbuse categories to our scam types
 */
function mapChainAbuseCategory(category) {
  const mapping = {
    'blackmail': 'Phishing',
    'darknet market': 'Other',
    'exchange fraud': 'Fake Exchange',
    'ransomware': 'Other',
    'sextortion': 'Other',
    'theft': 'Other',
    'other': 'Other'
  };

  return mapping[category?.toLowerCase()] || 'Other';
}

/**
 * Map CryptoScamDB types to our categories
 */
function mapCryptoScamDBType(type) {
  const mapping = {
    'scam': 'Other',
    'fake': 'Fake Exchange',
    'phishing': 'Phishing',
    'suspicious': 'Other'
  };

  return mapping[type?.toLowerCase()] || 'Other';
}
